/**
 * Canonical company data — single source of truth.
 * Sync manually: public/index.html (meta description, title) with service-area strings above.
 * JSON-LD LocalBusiness: src/seo/localBusinessJsonLd.js (injected in App.js).
 * Per-route title/description/OG: src/seo/pageMeta.js + src/components/PageMeta.jsx.
 */
export const COMPANY_NAME = "Refined Spaces";

/** Production site origin (schema, canonicals) */
export const COMPANY_WEBSITE_URL = "https://www.refinedspacesmarin.com";

export const COMPANY_INSTAGRAM_HANDLE = "@marinrefinedspaces";
export const COMPANY_INSTAGRAM_URL = "https://www.instagram.com/marinrefinedspaces";
export const COMPANY_YELP_URL = "https://yelp.to/gvw2jB5Ym3";
export const COMPANY_YELP_LABEL = "Yelp";

/**
 * Compact label — hero stats, contact “Service area” row, services CTA.
 * Prose sentences use COMPANY_SERVICE_AREA_LONG.
 */
export const COMPANY_SERVICE_AREA = "Marin, Sonoma & Napa";

/** Long form for body copy, e.g. “across Marin, Sonoma, and Napa counties” */
export const COMPANY_SERVICE_AREA_LONG = "Marin, Sonoma, and Napa counties";

/** JSON-LD areaServed (HomeAndConstructionBusiness) */
export const COMPANY_SCHEMA_AREAS_SERVED = [
  "Marin County, CA",
  "Sonoma County, CA",
  "Napa County, CA",
];

/** License line for badges, about, home CTA (e.g. CSLB #1132810) */
export const COMPANY_LICENSE = "CSLB #1132810";

/** Footer bottom bar license sentence */
export const COMPANY_FOOTER_LICENSE_LINE = `Licensed · Bonded · Insured · ${COMPANY_LICENSE}`;

export const COMPANY_PHONE_DISPLAY = "(415) 827-8879";
export const COMPANY_PHONE_HREF = "tel:+14158278879";

export const COMPANY_STREET = "1030 Railroad Ave, Unit A";
export const COMPANY_ADDRESS_LOCALITY = "Novato";
export const COMPANY_ADDRESS_REGION = "CA";
export const COMPANY_POSTAL_CODE = "94945";
export const COMPANY_CITY_STATE_ZIP = "Novato, CA 94945";
export const COMPANY_ADDRESS_LINE = "1030 Railroad Ave, Unit A, Novato, CA 94945";

/** Google Maps search for street address (Unit A) */
export const COMPANY_MAPS_SEARCH_URL =
  "https://www.google.com/maps/search/?api=1&query=1030+Railroad+Ave+Unit+A%2C+Novato%2C+CA+94945";

/** Pass to @react-google-maps/api <Autocomplete options={...} /> — suggestions limited to U.S. */
export const GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS = {
  componentRestrictions: { country: "us" },
};

/**
 * True when a Places `getPlace()` result is in the United States.
 * If `address_components` is missing, returns true (rely on `componentRestrictions: { country: "us" }`).
 */
export function googlePlaceIsUnitedStates(place) {
  const components = place?.address_components;
  if (!Array.isArray(components) || components.length === 0) return true;
  return components.some(
    (c) =>
      Array.isArray(c.types) &&
      c.types.includes("country") &&
      c.short_name === "US"
  );
}

/** Home page meta description (sync with PAGE_SEO.home / index.html) */
export const COMPANY_META_DESCRIPTION = `${COMPANY_NAME} — Luxury home remodeling across ${COMPANY_SERVICE_AREA_LONG}. Kitchens, bathrooms, ADUs & full renovations. Based in Novato. ${COMPANY_LICENSE}.`;
